import { useState } from 'react';
import {
  EmailField,
  InputField,
  Modal,
  Palette,
} from '@diamond-design/components';
import { CheckCircle, ChevronDown } from '@diamond-design/icons';
import CtaButton from '../ctaButton/ctaButton';
import US_STATES from './statesArray';
import styles from './newsLetterForm.module.scss';

const INITIAL_FORM_VALUES = {
  firstName: '',
  lastName: '',
  state: '',
  phone: '',
  companyName: '',
  email: '',
};

const FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  state: 'State',
  phone: 'Phone',
  companyName: 'Company name',
  email: 'Email address',
};

export default function NewsLetterForm({ headline, description, cta }) {
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const validateField = (name, value) => {
    const label = FIELD_LABELS[name] || name;

    if (!value?.trim()) return `${label} is required`;

    if (
      ['firstName', 'lastName', 'state', 'companyName'].includes(name) &&
      value.length < 2
    ) {
      return `${label} is required`;
    }

    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return !emailRegex.test(value) ? 'Enter a valid email address' : '';
    }

    if (name === 'phone') {
      const numericOnly = value.replace(/[^0-9]/g, '');
      return numericOnly.length !== 10 ? 'Phone number must be 10 digits' : '';
    }

    return '';
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    Object.entries(formValues).forEach(([key, value]) => {
      const errorMessage = validateField(key, value);
      if (errorMessage) {
        newErrors[key] = errorMessage;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setFormValues(INITIAL_FORM_VALUES);
    setErrors({});
  };

  const handleInputChange = ({ target: { name, value } }) => {
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }

    if (name === 'phone') {
      const numeric = value.replace(/\D/g, '').slice(0, 10);
      let formatted = numeric;
      if (numeric.length > 6) {
        formatted = `${numeric.slice(0, 3)}-${numeric.slice(
          3,
          6
        )}-${numeric.slice(6)}`;
      } else if (numeric.length > 3) {
        formatted = `${numeric.slice(0, 3)}-${numeric.slice(3)}`;
      }
      return setFormValues((prev) => ({ ...prev, phone: formatted }));
    }

    if (name === 'email') {
      return setFormValues((prev) => ({ ...prev, email: value }));
    }

    const maxLength = name === 'companyName' ? 200 : 50;
    const sanitizedValue =
      name === 'companyName'
        ? value.replace(/[^a-zA-Z0-9\s]/g, '').slice(0, maxLength)
        : value.replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength);

    setFormValues((prev) => ({ ...prev, [name]: sanitizedValue }));
    return '';
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const res = await fetch('/api/news-letter-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: formValues }),
    });

    if (res.ok) {
      setModalOpen(true);
      resetForm();
    }
  };

  const renderTextField = (label, type, name, Component = InputField) => {
    const error = errors[name];
    return (
      <Component
        id={name}
        style={styles.inputFieldContainer}
        label={label}
        type={type}
        name={name}
        value={formValues[name]}
        onChange={handleInputChange}
        required
        error={!!error}
        helperText={error}
        labelStyle={error ? { color: Palette.citrine[500] } : {}}
        inputStyle={`${styles.inputField} ${
          error ? styles.inputFieldError : ''
        }`}
        helperTextStyle={error ? { color: Palette.citrine[500] } : {}}
      />
    );
  };

  const renderSelectField = (label, name, options) => {
    const error = errors[name];
    return (
      <div className={`${styles.inputFieldContainer} ${styles.selectWrapper}`}>
        <label
          htmlFor={name}
          style={error ? { color: Palette.citrine[500] } : {}}
        >
          {label}
        </label>
        <select
          id={name}
          name={name}
          value={formValues[name]}
          onChange={handleInputChange}
          className={`${styles.inputField} ${
            error ? styles.inputFieldError : ''
          }`}
          required
        >
          <option value="">Select a state</option>
          {options.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
        {error && <div className={styles.helperText}>{error}</div>}
        <span
          className={`${styles.arrowPlacement} ${
            Object.keys(errors).length !== 0 ? styles.arrowTop : ''
          }`}
        >
          <ChevronDown color={Palette.gray[900]} width={25} height={25} />
        </span>
      </div>
    );
  };

  return (
    <div className={styles.modalForm}>
      {modalOpen && (
        <Modal open={modalOpen} size="xs" onDismiss={() => setModalOpen(false)}>
          <CheckCircle width={100} height={100} color={Palette.success[600]} />
          <div className={styles.modalContent}>
            <div className={styles.modalHeadline}>Thank you!</div>
            <div className={styles.modalDescription}>
              You'll now stay on top of consumer credit insights and news.
            </div>
          </div>
        </Modal>
      )}

      <div className={styles.left}>
        <h2 className={styles.headline}>{headline}</h2>
        <div className={styles.description}>{description}</div>
      </div>

      <div className={styles.right}>
        <div className={styles.formControl}>
          <div className={styles.inputRow}>
            {renderTextField('First Name', 'text', 'firstName')}
            {renderTextField('Last Name', 'text', 'lastName')}
          </div>

          <div className={styles.inputRow}>
            {renderSelectField('State', 'state', US_STATES)}
            {renderTextField('Phone', 'tel', 'phone')}
          </div>

          {renderTextField('Company Name', 'text', 'companyName')}
          {renderTextField('Email Address', 'email', 'email', EmailField)}

          {Object.keys(errors).length > 0 && (
            <div className={`${styles.error} ${styles.errorColor}`}>
              *Please fix all the errors to continue
            </div>
          )}

          <CtaButton
            className={styles.cta}
            label={cta?.fields?.label || 'Submit'}
            variant={cta?.fields?.variant || 'primary'}
            color={cta?.fields?.color || Palette.primary[500]}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}
